import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** The entity kinds with a global detail overlay of their own. */
export type DetailKind = "issue";

/**
 * How an open lands on the stack. `push` seats the new detail over whatever is
 * open, so the drill-in's close brings the layer beneath it back. `replace`
 * clears the other kinds first — a favorites row, a table cell, a search hit —
 * so a top-level open always shows what was clicked instead of mounting under
 * a standing drawer.
 */
export type DetailStackMode = "push" | "replace";

type RaiseNonces = Record<DetailKind, number>;

interface DetailStackActions {
  announceOpen: (kind: DetailKind, mode: DetailStackMode) => void;
  setPresented: (kind: DetailKind, presented: boolean) => void;
  registerDismiss: (kind: DetailKind, dismiss: () => void) => () => void;
}

// Two contexts, because they change on different schedules. The actions are
// stable for the provider's life: the presence effects depend on them, and a
// value that changed on every raise would re-run those effects and re-seat
// every presented kind in render order — undoing the raise it just made. The
// nonces are the one piece of state, read only by the dialog hosts.
const DetailStackActionsContext = createContext<DetailStackActions | null>(null);

/** Per kind: bumps when that kind's presented drawer must remount to reach the
 *  front. A portal's z-order is its mount order, so remounting is how a buried
 *  layer comes forward. Hosts key their dialog on it. */
const DetailStackNoncesContext = createContext<RaiseNonces>({ issue: 0 });

/** True inside a presented detail surface (a drawer, a fullscreen detail
 *  dialog, a create draft): an open from there pushes. */
const DetailStackHostContext = createContext(false);

/**
 * Coordinates the single-slot detail overlays as ONE z-ordered stack. Each
 * overlay keeps its own provider and state; this only tracks which kinds are
 * presented, bottom to top, and applies the two cross-kind rules a lone slot
 * cannot: a top-level open dismisses the other kinds (`replace`), and an open
 * of a kind already presented but buried brings it forward (a raise).
 * Same-kind opens swap inside their slot.
 *
 * Presence is reported by the providers from their live state, never inferred
 * from an open call — an open whose fetch fails never joins the order. Newly
 * presented is newly mounted, which is newly on top, so a report of `true`
 * seats the kind last.
 */
export function DetailStackProvider({ children }: { children: React.ReactNode }) {
  const order = useRef<DetailKind[]>([]);
  const dismissers = useRef(new Map<DetailKind, () => void>());
  const [raiseNonces, setRaiseNonces] = useState<RaiseNonces>({ issue: 0 });

  const registerDismiss = useCallback(
    (kind: DetailKind, dismiss: () => void) => {
      dismissers.current.set(kind, dismiss);
      return () => {
        if (dismissers.current.get(kind) === dismiss)
          dismissers.current.delete(kind);
      };
    },
    [],
  );

  const setPresented = useCallback((kind: DetailKind, presented: boolean) => {
    const without = order.current.filter((k) => k !== kind);
    order.current = presented ? [...without, kind] : without;
  }, []);

  const announceOpen = useCallback(
    (kind: DetailKind, mode: DetailStackMode) => {
      const current = order.current;
      if (mode === "replace") {
        // The dismissed kinds leave the order through their own presence
        // report, so the order never claims a drawer that is still closing.
        for (const other of current) {
          if (other !== kind) dismissers.current.get(other)?.();
        }
        return;
      }
      const index = current.indexOf(kind);
      if (index === -1 || index === current.length - 1) return;
      order.current = [...current.filter((k) => k !== kind), kind];
      setRaiseNonces((nonces) => ({ ...nonces, [kind]: nonces[kind] + 1 }));
    },
    [],
  );

  const actions = useMemo<DetailStackActions>(
    () => ({ announceOpen, setPresented, registerDismiss }),
    [announceOpen, setPresented, registerDismiss],
  );

  return (
    <DetailStackActionsContext.Provider value={actions}>
      <DetailStackNoncesContext.Provider value={raiseNonces}>
        {children}
      </DetailStackNoncesContext.Provider>
    </DetailStackActionsContext.Provider>
  );
}

/** Marks its subtree as a presented detail surface, so opens from inside it
 *  push onto the stack. Context flows through portals, so the shell that
 *  renders a detail popup wraps its children once and every chip, roster row
 *  and picker inside inherits it. */
export function DetailStackHost({ children }: { children: React.ReactNode }) {
  return (
    <DetailStackHostContext.Provider value>
      {children}
    </DetailStackHostContext.Provider>
  );
}

/**
 * The call-site half: a public open hook calls the returned function with its
 * kind right before delegating to its provider. The mode is read from where the
 * HOOK is called — inside a host it pushes, anywhere else it replaces — which
 * is why the providers themselves never announce: a provider sits above every
 * host and would read "outside" for every caller.
 */
export function useDetailStackAnnounce(): (kind: DetailKind) => void {
  const stack = useContext(DetailStackActionsContext);
  const inside = useContext(DetailStackHostContext);
  return useCallback(
    (kind: DetailKind) => {
      stack?.announceOpen(kind, inside ? "push" : "replace");
    },
    [stack, inside],
  );
}

/**
 * The provider half: reports whether `kind`'s VIEW presentation is live, and
 * while it is, lends the stack the close a `replace` open uses to clear it.
 * `dismiss` is read through a ref so an unstable close never churns the
 * registration.
 */
export function useDetailStackSlot(
  kind: DetailKind,
  presented: boolean,
  dismiss: () => void,
): void {
  const stack = useContext(DetailStackActionsContext);
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  });
  useEffect(() => {
    if (!stack) return;
    stack.setPresented(kind, presented);
    if (!presented) return;
    const unregister = stack.registerDismiss(kind, () => dismissRef.current());
    return () => {
      unregister();
      stack.setPresented(kind, false);
    };
  }, [stack, kind, presented]);
}

/** The key a kind's dialog host mounts its dialog on — see `raiseNonces`. */
export function useDetailStackRaiseNonce(kind: DetailKind): number {
  return useContext(DetailStackNoncesContext)[kind];
}
